import {
  EntityLinks,
  Goal,
  GoalSortingMode,
  ID,
  OrderDirection,
} from '@griply/types';

import {
  buildGoalsById,
  getGoalLifeAreas,
  GetGoalLifeAreasOptions,
} from './get-goal-life-area';

export type GetOrderedGoalIdsOptions = {
  // Life-area mode resolves inherited and relationship-linked life areas against these.
  allGoals: Goal[];
  links: EntityLinks;
};

const isGoalValueEmpty = (
  goal: Goal,
  sortMode: GoalSortingMode,
  lifeAreaOptions: GetGoalLifeAreasOptions | null,
): boolean => {
  if (sortMode === GoalSortingMode.Impact) {
    return goal.impact == null;
  }
  if (sortMode === GoalSortingMode.StartDate) {
    return !goal.startDate;
  }
  if (sortMode === GoalSortingMode.Deadline) {
    return !goal.deadline;
  }
  if (sortMode === GoalSortingMode.Progress) {
    return !goal.metric?.id;
  }
  if (sortMode === GoalSortingMode.LifeArea) {
    return !lifeAreaOptions || !getGoalLifeAreas(goal, lifeAreaOptions).length;
  }
  return false;
};

export const getOrderedGoalIds = (
  sortedGoals: Goal[],
  sortMode: GoalSortingMode | undefined,
  direction: OrderDirection,
  { allGoals, links }: GetOrderedGoalIdsOptions,
): ID[] => {
  const effectiveSortMode = sortMode ?? GoalSortingMode.StartDate;
  const lifeAreaOptions =
    effectiveSortMode === GoalSortingMode.LifeArea
      ? { goalsById: buildGoalsById(allGoals), links, cache: new Map() }
      : null;
  const withValue: Goal[] = [];
  const withoutValue: Goal[] = [];

  sortedGoals.forEach((goal) => {
    if (isGoalValueEmpty(goal, effectiveSortMode, lifeAreaOptions)) {
      withoutValue.push(goal);
      return;
    }
    withValue.push(goal);
  });

  const ordered =
    direction === OrderDirection.Desc ? withValue.slice().reverse() : withValue;

  return [...ordered, ...withoutValue].map(({ id }) => id);
};
